import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
} from "axios";

import type { ApiErrorPayload } from "@/types";

/**
 * Erreur API structurée propagée par le client.
 * Tous les hooks/services doivent l'attraper plutôt que `AxiosError`
 * pour rester découplés de l'implémentation HTTP.
 */
export class ApiError extends Error {
  public readonly status: number;
  public readonly code: string;
  public readonly details?: Record<string, unknown>;

  constructor(
    status: number,
    code: string,
    message: string,
    details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    if (details !== undefined) {
      this.details = details;
    }
  }
}

const baseURL: string = (() => {
  const fromEnv = import.meta.env.VITE_API_URL;
  if (typeof fromEnv === "string" && fromEnv.length > 0) {
    return fromEnv.replace(/\/+$/, "");
  }
  // Fallback : same-origin (proxy Vite en dev, Caddy en prod).
  return "";
})();

/**
 * Base d'URL de l'API (même valeur que `apiClient.baseURL`). Exposée pour les
 * navigations **pleine page** (`<a href>`) qui doivent viser le même backend
 * que les appels XHR — typiquement le flux Google OIDC en redirection serveur
 * (`GET /auth/google/login`), qui n'est PAS un fetch (chaîne de 302 vers Google,
 * cf. ADR-015). Chaîne vide en same-origin → URL relative résolue par le
 * navigateur contre l'origine courante.
 */
export const apiBaseUrl: string = baseURL;

export const apiClient: AxiosInstance = axios.create({
  baseURL,
  // Indispensable pour que le cookie httpOnly `sunmatch_anon` soit
  // envoyé/posé en cross-origin (cf. ADR-002).
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
  timeout: 15_000,
});

/**
 * Promesse partagée pour dédupliquer les appels concurrents à
 * `POST /sessions/anonymous`. Si 3 requêtes échouent simultanément avec
 * `ANONYMOUS_SESSION_REQUIRED`, on ne bootstrappe qu'**une** fois — les 2
 * autres attendent la même promesse, puis ré-essayent.
 */
let bootstrapInFlight: Promise<void> | null = null;

function bootstrapAnonymousSession(): Promise<void> {
  if (!bootstrapInFlight) {
    bootstrapInFlight = apiClient
      .post("/api/v1/sessions/anonymous")
      .then(() => undefined)
      .finally(() => {
        bootstrapInFlight = null;
      });
  }
  return bootstrapInFlight;
}

function extractErrorCode(data: unknown): string | undefined {
  if (!data || typeof data !== "object") return undefined;
  const obj = data as Record<string, unknown>;
  if (obj.error && typeof obj.error === "object" && "code" in obj.error) {
    return (obj.error as { code?: string }).code;
  }
  if (obj.detail && typeof obj.detail === "object" && "code" in obj.detail) {
    return (obj.detail as { code?: string }).code;
  }
  return undefined;
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiErrorPayload>) => {
    if (error.response) {
      const { status, data, config } = error.response;
      const code = extractErrorCode(data);

      // Retry automatique sur 401 ANONYMOUS_SESSION_{REQUIRED,UNKNOWN} :
      // on bootstrappe une session anonyme (POST /sessions/anonymous), puis
      // on rejoue la requête originale **une seule fois**. Évite la race
      // condition Dashboard au premier load (FIX-S4-REC-4).
      const original = config as
        | (AxiosRequestConfig & { _bootstrapRetried?: boolean })
        | undefined;
      const url = original?.url ?? "";
      const isBootstrapItself = url.endsWith("/sessions/anonymous");

      if (
        status === 401 &&
        !isBootstrapItself &&
        !original?._bootstrapRetried &&
        (code === "ANONYMOUS_SESSION_REQUIRED" ||
          code === "ANONYMOUS_SESSION_UNKNOWN")
      ) {
        try {
          await bootstrapAnonymousSession();
          if (original) {
            original._bootstrapRetried = true;
            return apiClient.request(original);
          }
        } catch {
          // Le bootstrap a échoué : on retombe sur la transformation
          // standard ApiError ci-dessous (l'utilisateur verra le 401 d'origine).
        }
      }

      if (data && typeof data === "object") {
        // Format AppError backend : { error: { code, message, details } }
        if ("error" in data && data.error) {
          return Promise.reject(
            new ApiError(
              status,
              data.error.code,
              data.error.message,
              data.error.details,
            ),
          );
        }
        // Format FastAPI HTTPException : { detail: { code, message, details } }
        const detail = (data as unknown as Record<string, unknown>).detail;
        if (detail && typeof detail === "object" && "code" in detail) {
          const d = detail as {
            code: string;
            message: string;
            details?: Record<string, unknown>;
          };
          return Promise.reject(
            new ApiError(status, d.code, d.message, d.details),
          );
        }
        if (typeof detail === "string") {
          return Promise.reject(new ApiError(status, "HTTP_ERROR", detail));
        }
      }
      return Promise.reject(
        new ApiError(status, "UNKNOWN_ERROR", error.message),
      );
    }
    if (error.request) {
      return Promise.reject(
        new ApiError(0, "NETWORK_ERROR", "Backend SunMatch injoignable."),
      );
    }
    return Promise.reject(new ApiError(0, "CLIENT_ERROR", error.message));
  },
);

/** Helper typé : effectue un GET et retourne directement le payload. */
export async function apiGet<T>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<T> {
  const response = await apiClient.get<T>(url, config);
  return response.data;
}

/** Helper typé : effectue un POST JSON et retourne le payload. */
export async function apiPost<TResponse, TBody = unknown>(
  url: string,
  body?: TBody,
  config?: AxiosRequestConfig,
): Promise<TResponse> {
  const response = await apiClient.post<TResponse>(url, body, config);
  return response.data;
}

/** Helper typé : effectue un PUT JSON et retourne le payload. */
export async function apiPut<TResponse, TBody = unknown>(
  url: string,
  body?: TBody,
  config?: AxiosRequestConfig,
): Promise<TResponse> {
  const response = await apiClient.put<TResponse>(url, body, config);
  return response.data;
}

/** Helper typé : effectue un PATCH JSON et retourne le payload. */
export async function apiPatch<TResponse, TBody = unknown>(
  url: string,
  body?: TBody,
  config?: AxiosRequestConfig,
): Promise<TResponse> {
  const response = await apiClient.patch<TResponse>(url, body, config);
  return response.data;
}

/** Helper typé : effectue un DELETE. */
export async function apiDelete<TResponse = void>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<TResponse> {
  const response = await apiClient.delete<TResponse>(url, config);
  return response.data;
}
